import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const Hero = () => {
  const [text, setText] = useState('');
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const roles = [
    'Tech Innovator',
    'Software Engineer',
    'Generative AI Enthusiast',
    'Prompt Engineer',
    'AI-first Problem Solver',
    'Automation Builder',
    'Solution Designer',
    'AI Tools Explorer',
    'Future-ready Technologist',
    'System Architect'
  ];

  useEffect(() => {
    const currentRole = roles[currentRoleIndex];
    
    const typewriterEffect = () => {
      if (!isDeleting && text.length < currentRole.length) {
        // Typing forward
        setText(currentRole.substring(0, text.length + 1));
      } else if (!isDeleting && text.length === currentRole.length) {
        // Finished typing, wait before deleting
        setTimeout(() => setIsDeleting(true), 2000);
        return;
      } else if (isDeleting && text.length > 0) {
        // Deleting
        setText(currentRole.substring(0, text.length - 1));
      } else if (isDeleting && text.length === 0) {
        // Finished deleting, move to next role
        setIsDeleting(false);
        setCurrentRoleIndex((prevIndex) => (prevIndex + 1) % roles.length);
        return;
      }
    };

    const speed = isDeleting ? 50 : 100; // Faster when deleting
    const timeoutId = setTimeout(typewriterEffect, speed);

    return () => clearTimeout(timeoutId);
  }, [text, isDeleting, currentRoleIndex, roles]);

  const scrollToAbout = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20 lg:pt-0">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(59,130,246,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.1),transparent_50%)]"></div>
      </div>

      {/* Circuit Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-32 h-32 border border-blue-400 rounded-full animate-pulse hidden lg:block"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 border border-purple-400 rounded-full animate-pulse delay-1000 hidden lg:block"></div>
        <div className="absolute top-1/2 left-10 w-16 h-16 border border-blue-300 rounded-full animate-pulse delay-500 hidden md:block"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          {/* Left Content */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <div className="mb-6">
              <h2 className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-400 mb-2 font-medium">
                Hello, I'm
              </h2>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 leading-tight">
                Swastik Ranjan
                <br />
                <span className="text-blue-400">Nanda</span>
              </h1>
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 mb-6 h-8 sm:h-10 md:h-12">
                <span className="text-blue-400">I'm a </span>
                <span className="text-white font-semibold">
                  {text}
                  <span className="animate-pulse">|</span>
                </span>
              </div>
            </div>

            <p className="text-gray-400 text-sm sm:text-base lg:text-lg mb-8 max-w-2xl leading-relaxed">
              6+ years of IT expertise specializing in innovative solutions, automation, and problem-solving. 
              From idea generation to deployment, I build tech solutions that make a positive impact.
            </p>

            <div className="flex justify-center lg:justify-start">
              <button
                onClick={scrollToAbout}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 text-sm sm:text-base"
              >
                Learn More About Me
              </button>
            </div>
          </div>

          {/* Right Content - Profile Image */}
          <div className="lg:w-1/2 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="relative z-10">
                <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-4 border-blue-400/30 shadow-2xl shadow-blue-500/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_d223c6e3-bc67-4352-b362-c457cc2ec1c9/artifacts/u4rvxo0r_Final1.png"
                    alt="Swastik Ranjan Nanda"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Decorative Elements - Hidden on mobile */}
              <div className="absolute -top-4 -right-4 w-16 h-16 sm:w-20 sm:h-20 border-2 border-blue-400 rounded-full animate-spin-slow opacity-60 hidden sm:block"></div>
              <div className="absolute -bottom-6 -left-6 w-12 h-12 sm:w-16 sm:h-16 border-2 border-purple-400 rounded-full animate-bounce opacity-60 hidden sm:block"></div>
              <div className="absolute top-1/2 -right-8 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-pulse opacity-40 hidden sm:block"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2">
        <button
          onClick={scrollToAbout}
          className="text-gray-400 hover:text-blue-400 transition-colors duration-300 animate-bounce"
        >
          <ChevronDown size={24} className="sm:w-8 sm:h-8" />
        </button>
      </div>
    </section>
  );
};

export default Hero;